import * as React from "react";
import { Skeleton } from "antd";
import { AppsAddIn20Regular, Branch20Regular, Chat20Regular, Dismiss16Regular, Link20Regular, Send16Regular, Wand20Regular } from "@fluentui/react-icons";
import { SearchRequests } from "../../services/SearchRequests";
import { REPERTOIRE, USAGETOPRODUCT } from "../../Consts";
import AiFeedback from "./ai/AiFeedback";
import AiGrid from "./ai/AiGrid";
import MatchSearchModal from "./ai/MatchSearchModal";
import { USAGE_MATCHING_PRODUCTS_SECTION_KEY, USAGE_MATCHING_WORKS_SECTION_KEY } from "../../Consts";
import { getPackageActionWorkItems, getSelectedPackageActionWorkItems, getSelectedUsageActionItems, getStreamStatusMessage, getUsageActionItems, normalizeResponse, renderAssistantMarkdown, updateMessage } from "./ai/AiSearchHelpers";
import type { IAiPackageActionItem } from "./ai/AiSearchHelpers";
import type { AiMatchModalConfig, AiMatchModalState, IAiFeedbackStateItem, IAiSearchViewProps, IAiSearchViewState, IAiSearchViewTableCodeState } from "./ai/AiSearchTypes";
import type { IMatchWorksSearchResult } from "../../types/usageTypes/IMatchWorksSearchResult";
import type { IProductSearchResult } from "../../types/IProductSearchResult";

const WORK_MATCH_FIELDS = [
    { name: "title", placeholder: "Title" },
    { name: "number", placeholder: "Number" },
    { name: "artist", placeholder: "Artist" },
    { name: "writers", placeholder: "Writers" }
];

const PRODUCT_MATCH_FIELDS = [
    { name: "title", placeholder: "Title" },
    { name: "number", placeholder: "Number" },
    { name: "artist", placeholder: "Artist" },
    { name: "contributor", placeholder: "Contributor" }
];

const pluralize = (count: number, word: string) => `${count} ${word}${count > 1 ? "s" : ""}`;

const toNumberedLines = (items: { id: string | number; title?: string }[]) =>
    items.map(({ id, title }, index) => `${index + 1}. ${title ? `${title} (${id})` : id}`).join("\n");

const toEntityLabel = (id: string | number, titles?: string[]) => {
    const title = titles?.filter(Boolean)[0];
    return title ? `${title} (${id})` : `${id}`;
};

class AiSearchView extends React.PureComponent<IAiSearchViewProps, IAiSearchViewState> {
    aiMessagesRef = React.createRef<HTMLDivElement>();
    aiPromptTextareaRef = React.createRef<HTMLTextAreaElement>();
    progressTimer: NodeJS.Timeout | null = null;
    tableCodeVisibility: IAiSearchViewTableCodeState = {};
    maxTextareaHeight = 200;
    progressStages = [
        "Understanding the question...",
        "Executing the query...",
        "Compiling the response data...",
        "Finalizing the results...",
        "Thinking more deeply..."
    ];
    state: IAiSearchViewState = {
        aiPrompt: "",
        aiMessages: [],
        inputAtBottom: false,
        tablePagination: {},
        conversationId: undefined,
        mode: "chat",
        feedbackState: {},
        tableSelection: {},
        matchModal: null,
    };

    componentDidUpdate = (prevProps: IAiSearchViewProps, prevState: IAiSearchViewState) => {
        if ((prevState.aiMessages?.length || 0) !== (this.state.aiMessages?.length || 0)) {
            const node = this.aiMessagesRef.current;
            if (node) {
                node.scrollTop = node.scrollHeight;
            }
        }

        if (prevState.aiPrompt !== this.state.aiPrompt) {
            this.adjustTextareaHeight();
        }
    };

    adjustTextareaHeight = () => {
        const textarea = this.aiPromptTextareaRef.current;
        if (!textarea)
            return;    

        textarea.style.height = "auto";
        textarea.style.height = `${Math.min(textarea.scrollHeight, this.maxTextareaHeight)}px`;
    };

    createMessageId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    updateAssistantMessage = (assistantMessageId: string, updates: any) => {
        this.setState(prevState => ({
            aiMessages: updateMessage(prevState.aiMessages || [], assistantMessageId, updates)
        }));
    };

    getPromptWithSource = (prompt: string) => {
        const dataSource = (this.props.dataSource || "").trim();
        return dataSource ? `${dataSource} ${prompt}` : prompt;
    };

    isRequestInProgress = () => (this.state.aiMessages || []).some(message => message.loading);

    handleSubmit = () => {
        if (this.props.searchDisabled || this.isRequestInProgress()) return;

        if (this.state.inputAtBottom) {
            this.runAiSearch(this.state.aiPrompt);
            return;
        }

        this.setState({ inputAtBottom: true }, () => this.runAiSearch(this.state.aiPrompt));
    };

    createAgentStreamHandler = (assistantMessageId: string) => {
        let latestSequenceNumber = -1;

        return (streamEvent: any) => {
            const eventSequenceNumber = Number(streamEvent?.data?.sequence_number ?? -1);
            if (!Number.isNaN(eventSequenceNumber) && eventSequenceNumber >= 0) {
                if (eventSequenceNumber < latestSequenceNumber) {
                    return;
                }
                latestSequenceNumber = eventSequenceNumber;
            }

            const statusMessage = getStreamStatusMessage(streamEvent);
            if (!statusMessage) {
                return;
            }

            this.updateAssistantMessage(assistantMessageId, { text: statusMessage });
        };
    };

    runAiSearch = async (prompt: string) => {
        const trimmedPrompt = (prompt || "").trim();
        if (!trimmedPrompt) return;

        const sourceDataSource = (this.props.dataSource || "").trim();
        const mode = this.state.mode;
        const promptWithSource = this.getPromptWithSource(trimmedPrompt);
        const userMessage = {
            id: this.createMessageId("user"),
            role: "user" as const,
            text: trimmedPrompt
        };
        const assistantMessageId = this.createMessageId("assistant");
        const assistantMessage = {
            id: assistantMessageId,
            role: "assistant" as const,
            loading: true,
            progressStage: 0,
            dataSource: sourceDataSource
        };

        this.setState(prevState => ({
            aiPrompt: "",
            aiMessages: [...(prevState.aiMessages || []), userMessage, assistantMessage]
        }), () => this.startProgressTimer(assistantMessageId));

        try {
            const onAgentEvent = this.createAgentStreamHandler(assistantMessageId);
            const response = mode === "agent"
                ? await SearchRequests.askGenieAgent(promptWithSource, this.props.pageName, this.state.conversationId, onAgentEvent)
                : await SearchRequests.askGenieChat(promptWithSource, this.props.pageName, this.state.conversationId);
            const { textContent, suggestedQuestions, aiTables } = normalizeResponse(response, sourceDataSource);
            const conversationId = response?.conversationId || this.state.conversationId;

            this.setState(prevState => ({
                aiMessages: updateMessage(prevState.aiMessages || [], assistantMessageId, {
                    loading: false,
                    text: textContent,
                    tables: aiTables,
                    suggestedQuestions,
                    progressStage: undefined,
                    serverMessageId: response?.messageId
                }),
                conversationId
            }), this.stopProgressTimer);
        } catch (error: any) {
            const message = error?.response?.data
                ? JSON.stringify(error.response.data, null, 2)
                : (error?.message || "AI request failed.");

            this.updateAssistantMessage(assistantMessageId, {
                loading: false,
                error: message,
                progressStage: undefined
            });
            this.stopProgressTimer();
        }
    };

    startProgressTimer = (assistantMessageId: string) => {
        this.stopProgressTimer();
        let stage = 0;
        this.progressTimer = setInterval(() =>
            this.setState(prevState => {
                stage = Math.min(stage + 1, 4);
                return {
                    aiMessages: prevState.aiMessages.map(msg =>
                        msg.id === assistantMessageId && msg.loading
                            ? { ...msg, progressStage: stage }
                            : msg
                    )
                };
            })
            , 5000);
    };

    stopProgressTimer = () => {
        if (this.progressTimer) {
            clearInterval(this.progressTimer);
            this.progressTimer = null;
        }
    };

    clearAiConversation = () => {
        this.setState({
            aiPrompt: "",
            aiMessages: [],
            inputAtBottom: false,
            conversationId: undefined,
        });
    };

    getFeedback = (messageId: string): IAiFeedbackStateItem => this.state.feedbackState[messageId] || {};

    updateFeedback = (messageId: string, updates: IAiFeedbackStateItem) => {
        this.setState(prevState => ({
            feedbackState: {
                ...prevState.feedbackState,
                [messageId]: {
                    ...(prevState.feedbackState[messageId] || {}),
                    ...updates
                }
            }
        }));
    };

    handlePositiveFeedback = (messageId: string, serverMessageId?: string) => {
        const feedbackMessageId = serverMessageId || messageId;
        SearchRequests.sendGenieFeedback(this.props.pageName, this.state.conversationId, feedbackMessageId, "POSITIVE", "");
        this.updateFeedback(messageId, { feedback: "up", comment: "", submitted: true });
    };

    handleNegativeFeedback = (messageId: string) => this.updateFeedback(messageId, { feedback: "down", submitted: false });

    handleFeedbackCommentChange = (messageId: string, value: string) => this.updateFeedback(messageId, { feedback: "down", comment: value });

    handleFeedbackSubmit = (messageId: string, serverMessageId?: string) => {
        const state = this.getFeedback(messageId);
        const feedbackMessageId = serverMessageId || messageId;
        const comment = String(state.comment || "");
        SearchRequests.sendGenieFeedback(this.props.pageName, this.state.conversationId, feedbackMessageId, "NEGATIVE", comment);
        this.updateFeedback(messageId, { submitted: true });
    };

    getAgentDisplayName = () => {
        const sourceEntityName = (this.props.pageName || this.props.dataSource || "").toLowerCase().trim();
        const entityName = ({ works: "Work", products: "Product", usages: "Usage" } as Record<string, string>)[sourceEntityName];
        return (<><strong>A</strong>r<strong>i</strong>a {entityName}</>);
    };

    setMode = (mode: "chat" | "agent") => {
        if (mode === this.state.mode) {
            return;
        }

        this.setState({ mode, conversationId: undefined });
    };

    setAgentPrompt = (aiPrompt: string, updates: Partial<IAiSearchViewState> = {}) => {
        const shouldResetConversation = this.state.mode !== "agent";

        const nextState = {
            ...updates,
            mode: "agent" as const,
            conversationId: shouldResetConversation ? undefined : this.state.conversationId,
            aiPrompt
        };

        this.setState(nextState as Pick<IAiSearchViewState, "mode" | "conversationId" | "aiPrompt">);
    };

    handleAddToPackageRecommendedAction = (workItems: IAiPackageActionItem[]) => {
        if (!workItems.length) {
            return;
        }

        const workLines = workItems
            .map(({ workId, title }, index) => `${index + 1}. ${title ? `${title} (${workId})` : workId}`)
            .join("\n");
        const prompt = `Add following works to package:\n${workLines}`;
        this.setAgentPrompt(prompt);
    };

    openMatchModal = (matchModal: AiMatchModalState) => {
        const items = matchModal.operation === "workMerge" ? matchModal.workItems : matchModal.usageItems;
        if (!items.length) {
            return;
        }

        this.setState({ matchModal });
    };

    closeMatchModal = () => {
        this.setState({ matchModal: null });
    };

    searchMatchWorks = (values: { [name: string]: string }) => this.props.searchMatchWorks?.({
        workIDs: [],
        title: values.title || "",
        number: values.number || "",
        artist: values.artist || "",
        writers: values.writers || "",
        societyAccountNumber: "",
        dataSource: "Repertoire",
        workBatchID: "",
        hasOpenWorkflow: false
    }, true);

    searchMatchProducts = (values: { [name: string]: string }) => this.props.searchMatchProducts?.({
        productIDs: [],
        title: values.title || "",
        number: values.number || "",
        artist: values.artist || "",
        contributor: values.contributor || "",
        dataSource: "Repertoire",
        productBatchID: "",
        productType: ""
    }, true);

    getMatchModalConfig = (matchModal: AiMatchModalState): AiMatchModalConfig => {
        switch (matchModal.operation) {
            case "usageToWork": {
                const usageLines = toNumberedLines(matchModal.usageItems.map(({ usageId, title }) => ({ id: usageId, title })));
                return {
                    title: `Match to work (${pluralize(matchModal.usageItems.length, "usage")} selected)`,
                    fields: WORK_MATCH_FIELDS,
                    searchResults: this.props.matchWorkSearchResult,
                    noResultsText: "No works found",
                    componentInstance: USAGE_MATCHING_WORKS_SECTION_KEY,
                    onSearch: this.searchMatchWorks,
                    onSelectResult: (work: IMatchWorksSearchResult) => {
                        if (!work?.workID) return;
                        this.setAgentPrompt(`Match following usage group(s) to work ${toEntityLabel(work.workID, work.title)}:\n${usageLines}`, { matchModal: null });
                    }
                };
            }
            case "usageToProduct": {
                const usageLines = toNumberedLines(matchModal.usageItems.map(({ usageId, title }) => ({ id: usageId, title })));
                return {
                    title: `Add usages to product (${pluralize(matchModal.usageItems.length, "usage")} selected)`,
                    fields: PRODUCT_MATCH_FIELDS,
                    searchResults: this.props.matchProductsSearchResult,
                    noResultsText: "No products found",
                    componentInstance: USAGE_MATCHING_PRODUCTS_SECTION_KEY,
                    onSearch: this.searchMatchProducts,
                    onSelectResult: (product: IProductSearchResult) => {
                        if (!product?.productCoreID) return;
                        this.setAgentPrompt(`Match following usage group(s) to product ${toEntityLabel(product.productCoreID, product.title)}:\n${usageLines}`, { matchModal: null });
                    }
                };
            }
            case "workMerge": {
                const sourceWorkIds = matchModal.workItems.map(({ workId }) => workId);
                const workLines = toNumberedLines(matchModal.workItems.map(({ workId, title }) => ({ id: workId, title })));
                return {
                    title: `Merge into work (${pluralize(matchModal.workItems.length, "work")} selected)`,
                    fields: WORK_MATCH_FIELDS,
                    searchResults: this.props.matchWorkSearchResult?.filter(work => !sourceWorkIds.includes(work.workID)),
                    noResultsText: "No works found",
                    componentInstance: USAGE_MATCHING_WORKS_SECTION_KEY,
                    onSearch: this.searchMatchWorks,
                    onSelectResult: (work: IMatchWorksSearchResult) => {
                        if (!work?.workID) return;
                        this.setAgentPrompt(`Merge following ${matchModal.dataSource} work into ${matchModal.dataSource} work ${toEntityLabel(work.workID, work.title)}:\n${workLines}`, { matchModal: null });
                    }
                };
            }
        }
    };

    handleOpenWorkDetailsFromMatch = (work: IMatchWorksSearchResult, event?: React.MouseEvent) => {
        this.props.openWorkSearchResult?.(work, event);
        this.closeMatchModal();
    };

    handleOpenProductDetailsFromMatch = (product: IProductSearchResult, event?: React.MouseEvent) => {
        this.props.openProductSearchResult?.(product, event);
        this.closeMatchModal();
    };

    handleTablePaginationChange = (tableKey: string, page: number, pageSize: number) => {
        this.setState(prevState => ({
            tablePagination: {
                ...prevState.tablePagination,
                [tableKey]: { page, pageSize }
            }
        }));
    };

    handleTableSelectionChange = (tableKey: string, selectedRowKeys: (string | number)[]) => {
        this.setState(prevState => ({
            tableSelection: {
                ...prevState.tableSelection,
                [tableKey]: selectedRowKeys
            }
        }));
    };

    toggleTableCode = (tableKey: string) => {
        this.tableCodeVisibility[tableKey] = !this.tableCodeVisibility[tableKey];
        this.forceUpdate();
    };

    render() {
        const { searchDisabled } = this.props;
        const { mode } = this.state;

        return (
            <div className="row searchView">
                <div className={`col-xs-12 aiChatPanel${this.state.inputAtBottom ? ' inputAtBottom' : ''}`}>
                    <div className={`aiChatMessages${this.state.inputAtBottom ? ' inputAtBottom' : ''}`} ref={this.aiMessagesRef}>
                        {(this.state.aiMessages || []).map((message, messageIndex) => {
                            if (message.role === "user") {
                                return (
                                    <div key={message.id} className="aiChatBubble aiUserBubble">
                                        <div className="aiBubbleLabel"><strong>You</strong></div>
                                        <div className="aiBubbleContent">{message.text}</div>
                                    </div>
                                );
                            }

                            return (
                                <div key={message.id} className="aiChatBubble aiAssistantBubble">
                                    <div className="aiBubbleLabel">{this.getAgentDisplayName()}</div>
                                    {message.loading ? (
                                        <div className="aiBubbleContent aiProgressText">
                                            <span className="aiProgressTextAnimated">
                                                {message.text || this.progressStages[(message.progressStage ?? 0)]}
                                            </span>
                                            <Skeleton active paragraph={{ rows: 3 }} title={false} style={{ marginTop: 5 }} />
                                        </div>
                                    ) : message.error ? (
                                        <div className="aiBubbleContent aiErrorText">{message.error}</div>
                                    ) : (
                                        <>
                                            {message.text && <div className="aiBubbleContent">{renderAssistantMarkdown(message.text)}</div>}
                                            {message.tables && message.tables.length > 0 && (
                                                <AiGrid
                                                    tables={message.tables}
                                                    pageName={this.props.pageName}
                                                    dataSource={this.props.dataSource}
                                                    addToPackages={this.props.addToPackages}
                                                    packageResultPageData={this.props.packageResultPageData}
                                                    getWorkDetails={this.props.getWorkDetails}
                                                    getProductDetails={this.props.getProductDetails}
                                                    getUsageDetails={this.props.getUsageDetails}
                                                    tablePagination={this.state.tablePagination}
                                                    tableCodeVisibility={this.tableCodeVisibility}
                                                    tableSelection={this.state.tableSelection}
                                                    onTablePaginationChange={this.handleTablePaginationChange}
                                                    onToggleTableCode={this.toggleTableCode}
                                                    onSelectionChange={this.handleTableSelectionChange}
                                                />
                                            )}
                                            {message.suggestedQuestions && message.suggestedQuestions.length > 0 && (
                                                <div className="aiSuggestions">
                                                    <div className="aiSectionTitle">Suggested follow-ups</div>
                                                    <div className="aiSuggestionChips">
                                                        {message.suggestedQuestions.map((question, index) => (
                                                            <button
                                                                key={`ai-suggestion-${messageIndex}-${index}`}
                                                                type="button"
                                                                className="aiSuggestionChip"
                                                                onClick={() => this.setState({ aiPrompt: question })}
                                                            >
                                                                <span className="aiSuggestionChipIcon">↪</span>{question}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {(() => {
                                                const candidateWorkItems = this.props.addToPackages && this.props.packageResultPageData
                                                    ? getPackageActionWorkItems(message.tables, this.props.pageName)
                                                    : [];

                                                if (candidateWorkItems.length === 0) {
                                                    return null;
                                                }

                                                const selectedWorkItems = getSelectedPackageActionWorkItems(message.tables, this.props.pageName, this.state.tableSelection);

                                                return (
                                                    <div className="aiSuggestions">
                                                        <div className="aiSectionTitle">Suggested actions</div>
                                                        <div className="aiSuggestionChips">
                                                            <button
                                                                type="button"
                                                                className="aiSuggestionChip"
                                                                onClick={() => this.handleAddToPackageRecommendedAction(selectedWorkItems)}
                                                                disabled={selectedWorkItems.length === 0}
                                                            >
                                                                <AppsAddIn20Regular className="aiSuggestionChipIcon" aria-hidden="true" />
                                                                {selectedWorkItems.length > 0
                                                                    ? `Add ${selectedWorkItems.length > 1 ? `${selectedWorkItems.length} works` : "work"} to package`
                                                                    : "Select works to add to package"}
                                                            </button>

                                                            {(message.dataSource ?? this.props.dataSource) === REPERTOIRE && (
                                                                <button
                                                                    type="button"
                                                                    className="aiSuggestionChip"
                                                                    onClick={() => this.openMatchModal({ operation: "workMerge", dataSource: REPERTOIRE, workItems: selectedWorkItems })}
                                                                    disabled={selectedWorkItems.length != 1}
                                                                >
                                                                    <Branch20Regular className="aiSuggestionChipIcon" aria-hidden="true" />
                                                                        {selectedWorkItems.length != 1 ? `Select a work to merge` : `Search for work to merge into`}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            {(() => {
                                                const isProductTab = (message.dataSource ?? this.props.dataSource) === USAGETOPRODUCT;
                                                const canMatchToWork = !isProductTab && this.props.searchMatchWorks;
                                                const canMatchToProduct = isProductTab && this.props.searchMatchProducts;

                                                const candidateUsageItems = canMatchToWork || canMatchToProduct
                                                    ? getUsageActionItems(message.tables, this.props.pageName)
                                                    : [];

                                                if (candidateUsageItems.length === 0) {
                                                    return null;
                                                }

                                                const selectedUsageItems = getSelectedUsageActionItems(message.tables, this.props.pageName, this.state.tableSelection);

                                                return (
                                                    <div className="aiSuggestions">
                                                        <div className="aiSectionTitle">Suggested actions</div>
                                                        <div className="aiSuggestionChips">
                                                            <button
                                                                type="button"
                                                                className="aiSuggestionChip"
                                                                onClick={() => this.openMatchModal({
                                                                    operation: isProductTab ? "usageToProduct" : "usageToWork",
                                                                    usageItems: selectedUsageItems
                                                                })}
                                                                disabled={selectedUsageItems.length === 0 }
                                                            >
                                                                <Link20Regular className="aiSuggestionChipIcon" aria-hidden="true" />
                                                                {selectedUsageItems.length > 0
                                                                    ? (isProductTab
                                                                        ? `Add ${selectedUsageItems.length > 1 ? `${selectedUsageItems.length} usages` : "usage"} to product`
                                                                        : `Match ${selectedUsageItems.length > 1 ? `${selectedUsageItems.length} usages` : "usage"} to work`)
                                                                    : (isProductTab ? "Select usages to add to product" : "Select usages to match to work")}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            <AiFeedback
                                                message={message}
                                                feedback={this.getFeedback(message.id)}
                                                onPositive={this.handlePositiveFeedback}
                                                onNegative={this.handleNegativeFeedback}
                                                onCommentChange={this.handleFeedbackCommentChange}
                                                onSubmitNegative={this.handleFeedbackSubmit}
                                            />
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    
                    <div className={`aiChatInputRow${this.state.inputAtBottom ? ' inputAtBottom' : ''}`}>
                        <div className={`aiChatInputBox${this.state.inputAtBottom ? ' inputAtBottom' : ''}${mode === "agent" ? ' agentMode' : ''}`}>
                            <textarea
                                ref={this.aiPromptTextareaRef}
                                className={`aiChatInputTextarea${this.state.inputAtBottom ? ' inputAtBottom' : ''}`}
                                value={this.state.aiPrompt}
                                onChange={e => this.setState({ aiPrompt: e.target.value })}
                                disabled={searchDisabled === true}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey && !searchDisabled) {
                                        e.preventDefault();
                                        this.handleSubmit();
                                    }
                                }}
                                placeholder="Ask your question..."
                                maxLength={1000}
                                rows={1}
                            />
                            <div className="aiChatInputControls">
                                <div className="aiModeSwitch" role="group" aria-label="AI mode selector">
                                    <button
                                        type="button"
                                        title="Chat mode"
                                        aria-label="Chat mode"
                                        className={`aiModeSwitchBtn ${mode === "chat" ? "active" : ""}`}
                                        onClick={() => this.setMode("chat")}
                                        disabled={searchDisabled === true}
                                    >
                                        <Chat20Regular className="aiModeIcon" aria-hidden="true" />
                                        <span>Chat</span>
                                    </button>
                                    <button
                                        type="button"
                                        title="Agent mode"
                                        aria-label="Agent mode"
                                        className={`aiModeSwitchBtn ${mode === "agent" ? "active" : ""}`}
                                        onClick={() => this.setMode("agent")}
                                        disabled={searchDisabled === true}
                                    >
                                        <Wand20Regular className="aiModeIcon" aria-hidden="true" />
                                        <span>Agent</span>
                                    </button>
                                </div>
                                <div className="aiChatActionButtons">
                                    <button
                                        type="button"
                                        title="Send"
                                        onClick={this.handleSubmit}
                                        disabled={searchDisabled === true || this.isRequestInProgress() || !this.state.aiPrompt.trim()}
                                        className="aiChatInputSendBtn"
                                        aria-label="Send"
                                        role="button"
                                    >
                                        <Send16Regular aria-hidden="true" />
                                    </button>
                                    <button
                                        type="button"
                                        title="Clear"
                                        onClick={this.clearAiConversation}
                                        disabled={searchDisabled === true}
                                        className="aiChatInputClearBtn"
                                        aria-label="Clear"
                                        role="button"
                                    >
                                        <Dismiss16Regular aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="aiDisclaimerText">Responses are AI generated. Always review their accuracy.</div>
                    </div>
                </div>
                {this.state.matchModal && (
                    <MatchSearchModal
                        key={this.state.matchModal.operation}
                        open
                        {...this.getMatchModalConfig(this.state.matchModal)}
                        searchResultsTableData={this.props.searchResultsTableData}
                        onClose={this.closeMatchModal}
                        onOpenWorkDetails={this.handleOpenWorkDetailsFromMatch}
                        onOpenProductDetails={this.handleOpenProductDetailsFromMatch}
                    />
                )}
            </div>
        );
    }
}

export default AiSearchView;
